/**
 * FormsTab — the target user's lead forms: list, publish toggle, and entry
 * point into FormBuilderDrawer (create + edit). Each form is a public page at
 * fhl.one/f/{slug} — usually reached through a QR code's tracking link, but
 * the URL works anywhere (email signatures, social bios).
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Center,
  Heading,
  HStack,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Switch,
  Tag,
  Text,
  Tooltip,
  VStack,
  useDisclosure,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCopy,
  FiExternalLink,
  FiFileText,
} from 'react-icons/fi';
import FormBuilderDrawer from './FormBuilderDrawer';
import { call, formPublicUrl, newFormDraft, rowToDraft } from './qrStudioUtils';

export default function FormsTab({ targetUser, targetUserId, forms, bookingPages, perForm, onChanged }) {
  const toast = useToast();
  const builder = useDisclosure();
  const [builderDraft, setBuilderDraft] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const cancelRef = useRef();
  const cardBg = useColorModeValue('white', 'gray.800');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');

  const openCreate = () => {
    setBuilderDraft(newFormDraft(targetUser));
    builder.onOpen();
  };

  const openEdit = (form) => {
    setBuilderDraft(rowToDraft(form));
    builder.onOpen();
  };

  const copyLink = (form) => {
    navigator.clipboard.writeText(formPublicUrl(form.slug));
    toast({ title: 'Form link copied', description: formPublicUrl(form.slug), status: 'success', duration: 2500 });
  };

  const handleToggle = async (form, isActive) => {
    try {
      await call('manageLeadForms', { userId: targetUserId, action: 'toggle_active', formId: form.id, isActive });
      onChanged();
    } catch (err) {
      toast({ title: 'Toggle failed', description: err.message, status: 'error', duration: 5000 });
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await call('manageLeadForms', { userId: targetUserId, action: 'delete', formId: pendingDelete.id });
      toast({ title: 'Form deleted', status: 'success', duration: 3000 });
      setPendingDelete(null);
      onChanged();
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <HStack justify="space-between" mb={5}>
        <Text color="gray.500" fontSize="sm">
          Mobile-first landing pages that create CRM contacts — point a QR code at one, or share the link directly.
        </Text>
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={openCreate} flexShrink={0}>
          New Form
        </Button>
      </HStack>

      {forms.length === 0 ? (
        <Center borderWidth="2px" borderStyle="dashed" borderColor={cardBorder} borderRadius="xl" py={16} flexDir="column">
          <Heading size="sm" color="gray.500" mb={2}>No forms yet</Heading>
          <Text color="gray.400" fontSize="sm" mb={4}>
            "Fill out your info to connect" / "Enter the raffle" — build it once, point QR codes at it.
          </Text>
          <Button leftIcon={<FiPlus />} colorScheme="blue" variant="outline" onClick={openCreate}>
            Create your first form
          </Button>
        </Center>
      ) : (
        <VStack spacing={3} align="stretch">
          {forms.map((form) => {
            const stats = perForm?.[form.id];
            return (
              <Box
                key={form.id}
                bg={cardBg}
                borderWidth="1px"
                borderColor={cardBorder}
                borderRadius="xl"
                p={4}
                opacity={form.is_active ? 1 : 0.6}
              >
                <HStack justify="space-between" align="start" spacing={4} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
                  <HStack align="start" spacing={3} flex={1} minW="240px">
                    <Center
                      boxSize="42px"
                      borderRadius="lg"
                      flexShrink={0}
                      style={{ backgroundColor: form.accent_color || '#1A365D' }}
                    >
                      <FiFileText color="white" size={20} />
                    </Center>
                    <VStack align="start" spacing={0.5}>
                      <HStack>
                        <Text fontWeight="bold">{form.title}</Text>
                        {!form.is_active && <Tag size="sm" colorScheme="gray">Paused</Tag>}
                        {stats?.failed > 0 && (
                          <Tooltip label={`${stats.failed} submission(s) failed to sync to the CRM`}>
                            <Tag size="sm" colorScheme="red">{stats.failed} sync issue{stats.failed > 1 ? 's' : ''}</Tag>
                          </Tooltip>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="gray.400" fontFamily="mono">fhl.one/f/{form.slug}</Text>
                      <HStack spacing={3} pt={1}>
                        <Text fontSize="sm">
                          <b>{stats?.submissions || 0}</b>{' '}
                          <Text as="span" color="gray.500" fontSize="xs">submissions</Text>
                        </Text>
                        {stats?.lastSubmittedAt && (
                          <Text fontSize="xs" color="gray.400">
                            last {new Date(stats.lastSubmittedAt).toLocaleString()}
                          </Text>
                        )}
                      </HStack>
                    </VStack>
                  </HStack>

                  <HStack spacing={1} flexShrink={0}>
                    <Tooltip label="Copy public link">
                      <IconButton icon={<FiCopy />} size="sm" variant="ghost" aria-label="Copy link" onClick={() => copyLink(form)} />
                    </Tooltip>
                    <Tooltip label="Open public page">
                      <IconButton
                        icon={<FiExternalLink />}
                        size="sm"
                        variant="ghost"
                        aria-label="Open form"
                        onClick={() => window.open(`/f/${form.slug}`, '_blank')}
                      />
                    </Tooltip>
                    <Tooltip label="Edit in builder">
                      <IconButton icon={<FiEdit2 />} size="sm" variant="ghost" aria-label="Edit" onClick={() => openEdit(form)} />
                    </Tooltip>
                    <Tooltip label="Delete">
                      <IconButton
                        icon={<FiTrash2 />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        aria-label="Delete"
                        onClick={() => setPendingDelete(form)}
                      />
                    </Tooltip>
                    <Switch
                      size="sm"
                      ml={2}
                      isChecked={form.is_active !== false}
                      onChange={(e) => handleToggle(form, e.target.checked)}
                    />
                  </HStack>
                </HStack>
              </Box>
            );
          })}
        </VStack>
      )}

      {builderDraft && (
        <FormBuilderDrawer
          isOpen={builder.isOpen}
          onClose={builder.onClose}
          targetUserId={targetUserId}
          initialDraft={builderDraft}
          bookingPages={bookingPages}
          onSaved={() => { builder.onClose(); onChanged(); }}
        />
      )}

      <AlertDialog isOpen={!!pendingDelete} leastDestructiveRef={cancelRef} onClose={() => setPendingDelete(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Delete form</AlertDialogHeader>
            <AlertDialogBody>
              Delete <b>{pendingDelete?.title}</b>? Its submission history is removed with it, and any QR codes
              pointing here will show a "link unavailable" page. CRM contacts already created are NOT affected.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setPendingDelete(null)}>Cancel</Button>
              <Button colorScheme="red" onClick={handleDelete} ml={3} isLoading={deleting}>Delete</Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
